import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import { additionalIncomePredicate } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import type { OtherIncomeGroupFacade } from "@aprao/model/src/stores/revenue/OtherIncomeGroupFacade.js";
import type { SalesTaxConfigurationFacade } from "@aprao/model/src/stores/SalesTaxConfigurationFacade.js";
import type { TaxFacade } from "@aprao/model/src/stores/TaxFacade.js";
import { observer } from "mobx-react-lite";
import { mapForNullable } from "option-t/nullable";

import { useI18n } from "../../../common/ApraoI18n.js";
import { useNumberUnits } from "../../../common/useNumberUnits.js";
import { buildRevenueItemDescription } from "../../../i18n/buildRevenueItemDescription.js";
import { buildSalesTaxName } from "../../../i18n/buildSalesTaxName.js";
import { SimpleTableRow, SimpleTableSection } from "../../SimpleTableRow.js";

export const OtherTableSection = observer(function OtherTableSection(props: {
  label: string;
  revenueGroup: OtherIncomeGroupFacade;
  salesTaxConfiguration: SalesTaxConfigurationFacade | null;
  tax: TaxFacade;
}) {
  const { label, revenueGroup, salesTaxConfiguration, tax } = props;

  const { msg, effectiveLocale } = useI18n();
  const { currency } = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[label, null, null, null, null, `Total (${currency.symbol})`]}
      />
      {revenueGroup.items.map((item, itemIdx) => (
        <SimpleTableRow
          key={itemIdx}
          cells={[
            buildRevenueItemDescription(effectiveLocale, item.$model),
            null,
            null,
            null,
            null,
            mapForNullable(item.aggregateValue, formatDecimalNumber),
          ]}
        />
      ))}

      {salesTaxConfiguration && !additionalIncomePredicate(revenueGroup) && (
        <>
          <SimpleTableRow
            bold
            borderBottom
            borderTop
            cells={[
              "Gross revenue",
              null,
              null,
              null,
              null,
              formatDecimalNumber(revenueGroup.valueGross ?? FRACTION_ZERO),
            ]}
          />
          <SimpleTableRow
            cells={[
              buildSalesTaxName(salesTaxConfiguration.$model),
              null,
              formatPercentage(tax.gdvEffectiveSalesTaxRate ?? FRACTION_ZERO),
              null,
              null,
              formatDecimalNumber(
                tax.getTaxAmountByRevenueGroup(revenueGroup) ?? FRACTION_ZERO,
              ),
            ]}
          />
        </>
      )}
      <SimpleTableRow
        bold
        borderBottom
        borderTop
        thickerBorder
        cells={[
          msg("global.netRevenue"),
          null,
          null,
          null,
          null,
          formatDecimalNumber(
            tax.getValueNetByRevenueGroup(revenueGroup) ?? FRACTION_ZERO,
          ),
        ]}
      />
    </SimpleTableSection>
  );
});
